import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Loader2, SlidersHorizontal } from "lucide-react";

const CATEGORIES = ["All", "Hoodies", "Oversized Tees", "Cargo Pants", "Outerwear", "Accessories"];
const SORTS = [
  { id: "new", label: "Newest" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("new");

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category && category !== "All") p.category = category;
    api.get("/products", { params: p })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    return arr;
  }, [products, sort]);

  const setCategory = (c) => {
    if (c === "All") { params.delete("category"); } else { params.set("category", c); }
    setParams(params);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10" data-testid="catalog-page">
      <div className="mb-8">
        <span className="font-mono text-[11px] uppercase tracking-widest text-brand">The Collection</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mt-1">
          {category === "All" ? "All Products" : category}
        </h1>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 border-y border-zinc-900 py-4">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                category === c ? "bg-brand text-black border-brand" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <SlidersHorizontal className="h-4 w-4" />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            className="bg-transparent font-mono text-[11px] uppercase tracking-widest focus:outline-none cursor-pointer"
          >
            {SORTS.map((s) => <option key={s.id} value={s.id} className="bg-zinc-900">{s.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : sorted.length === 0 ? (
        <p className="text-center py-24 font-mono text-xs uppercase tracking-widest text-zinc-500">No products found</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-4">
          {sorted.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
